import { useState, useEffect, useRef } from 'react';
import { adminLog, adminLogError } from '../../../lib/adminLog';

const pendingContentWrites = new Set<Promise<void>>();

export async function flushContentWrites(): Promise<void> {
  await Promise.all(Array.from(pendingContentWrites));
}

// Function to merge default values with existing data
function mergeWithDefaults(defaults: any, existing: any): any {
  if (!existing || typeof existing !== 'object') {
    return defaults;
  }
  
  const result = { ...defaults };
  
  for (const key in existing) {
    if (existing[key] !== undefined && existing[key] !== null) {
      if (typeof defaults[key] === 'object' && !Array.isArray(defaults[key]) && defaults[key] !== null) {
        result[key] = mergeWithDefaults(defaults[key], existing[key]);
      } else {
        result[key] = existing[key];
      }
    }
  }
  
  return result;
}

export function useContentStore<T>(section: string, initialValue: T) {
  const initialValueRef = useRef(initialValue);
  const dataRef = useRef(initialValue);
  const writeQueueRef = useRef<Promise<void>>(Promise.resolve());
  const [data, setData] = useState<T>(initialValue);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    fetch(`/api/admin/content/${encodeURIComponent(section)}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Unable to load ${section}`);
        return response.json();
      })
      .then((docData) => {
        adminLog('content.loaded', { section });
        const existingData = docData.data || {};
        // Merge existing data with default values to ensure all fields exist
        const mergedData = mergeWithDefaults(initialValueRef.current, existingData);
        dataRef.current = mergedData;
        setData(mergedData);
      })
      .catch((error) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        adminLogError('content.load_failed', error, { section });
        setData(initialValueRef.current);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [section]);

  const updateData = (newData: T | ((prev: T) => T)) => {
    const valueToStore = newData instanceof Function ? newData(dataRef.current) : newData;
    dataRef.current = valueToStore;
    setData(valueToStore);

    const write = async () => {
      adminLog('content.save_started', { section });
      const response = await fetch(`/api/admin/content/${encodeURIComponent(section)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: valueToStore })
      });
      if (!response.ok) throw new Error(`Unable to save ${section}`);
      adminLog('content.save_succeeded', { section });
    };

    const operation = writeQueueRef.current.then(write, write);
    pendingContentWrites.add(operation);
    operation.then(
      () => pendingContentWrites.delete(operation),
      () => pendingContentWrites.delete(operation)
    );
    writeQueueRef.current = operation.catch((error) => {
      adminLogError('content.save_failed', error, { section });
    });
    return operation;
  };

  return [data, updateData, loading] as const;
}
